import { clientTokenCache } from './ClientTokenCache';
import { dspAuthService } from './DSPAuthService';
import { fetchWithTimeout } from './fetchWithTimeout';

const AUTH_ERROR_CODES = new Set(['session_expired', 'token_refresh_failed']);

let refreshInFlight = null;

async function attemptSessionRefresh() {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = dspAuthService
    .getSession()
    .then(session => {
      if (!session?.isAuthenticated) {
        return false;
      }
      clientTokenCache.invalidate();
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

function fireAuthExpired(code = 'session_expired') {
  clientTokenCache.invalidate();
  window.dispatchEvent(
    new CustomEvent('dspAuthExpired', { detail: { code } }),
  );
}

function extractErrorCode(body) {
  if (!body || typeof body !== 'object') {
    return null;
  }
  return body.code || null;
}

/**
 * Wraps fetch for authenticated endpoints that rely on the bkfc_session cookie.
 * On a 401 response, attempts a single session refresh and retries the request.
 * If refresh fails or retry still returns 401, fires dspAuthExpired to redirect
 * the user to login.
 */
export async function authFetch(url, options = {}) {
  const mergedOptions = { credentials: 'include', ...options };
  const response = await fetchWithTimeout(url, mergedOptions);

  if (response.status !== 401) {
    return response;
  }

  const clonedBody = await response.clone().json().catch(() => null);
  const errorCode = extractErrorCode(clonedBody);

  if (errorCode && AUTH_ERROR_CODES.has(errorCode)) {
    fireAuthExpired(errorCode);
    return response;
  }

  const refreshed = await attemptSessionRefresh();
  if (!refreshed) {
    fireAuthExpired('session_expired');
    return response;
  }

  const retryResponse = await fetchWithTimeout(url, mergedOptions);
  if (retryResponse.status === 401) {
    fireAuthExpired('session_expired');
  }

  return retryResponse;
}
